const SESSION_LOCAL_STORAGE_KEY = 'monkey-session';
const SESSION_COOKIE_KEY = 'monkey-session';

// In-memory fallback storage
const memoryStorage: { sessionId?: string; expiry?: number } = {};

const getQueryParams = () => {
  return new URLSearchParams(window.location.search);
};

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Helper functions for different storage methods
const getFromCookie = (key: string): string | null => {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === key) {
        const decoded = decodeURIComponent(value);
        const parsed = JSON.parse(decoded);
        if (parsed.expiry && Date.now() > parsed.expiry) {
          // Expired, remove it
          document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          return null;
        }
        return parsed.sessionId;
      }
    }
  } catch (e) {
    // Ignore cookie errors
  }
  return null;
};

const setToCookie = (key: string, sessionId: string) => {
  try {
    const expiry = Date.now() + 3 * 60 * 60 * 1000; // 3 hours
    const item = { sessionId, expiry };
    const encoded = encodeURIComponent(JSON.stringify(item));
    document.cookie = `${key}=${encoded}; path=/; max-age=${3 * 60 * 60}`; // 3 hours
  } catch (e) {
    // Ignore cookie errors
  }
};

const getFromSessionStorage = (key: string): string | null => {
  try {
    const result = sessionStorage.getItem(key);
    if (!result) return null;

    const parsed = JSON.parse(result);
    const now = Date.now();

    if (now > parsed.expiry) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsed.sessionId;
  } catch (e) {
    return null;
  }
};

const setToSessionStorage = (key: string, sessionId: string) => {
  try {
    const expiry = Date.now() + 3 * 60 * 60 * 1000; // 3 hours
    const item = { sessionId, expiry };
    sessionStorage.setItem(key, JSON.stringify(item));
  } catch (e) {
    // Ignore sessionStorage errors
  }
};

export const getSessionId = (): string | null => {
  // Try localStorage first (most persistent)
  try {
    const result = localStorage.getItem(SESSION_LOCAL_STORAGE_KEY);
    if (result) {
      const parsed = JSON.parse(result);
      const now = Date.now();

      if (now <= parsed.expiry) {
        return parsed.sessionId;
      }
      localStorage.removeItem(SESSION_LOCAL_STORAGE_KEY);
    }
  } catch (e) {
    // localStorage failed, try other methods
  }

  // Try sessionStorage (session-persistent)
  const sessionResult = getFromSessionStorage(SESSION_LOCAL_STORAGE_KEY);
  if (sessionResult) return sessionResult;

  // Try cookies (cross-tab persistent)
  const cookieResult = getFromCookie(SESSION_COOKIE_KEY);
  if (cookieResult) return cookieResult;

  // Try memory storage (current page only)
  if (memoryStorage.sessionId && memoryStorage.expiry && Date.now() <= memoryStorage.expiry) {
    return memoryStorage.sessionId;
  }

  // Check URL parameters for existing session
  const urlParams = getQueryParams();
  const urlSessionId = urlParams.get('session_id');
  if (urlSessionId) {
    // Store it in our fallback methods for next time
    memoryStorage.sessionId = urlSessionId;
    memoryStorage.expiry = Date.now() + 24 * 60 * 60 * 1000;
    return urlSessionId;
  }

  return null;
};

export const setSessionId = (sessionId: string) => {
  const expiry = Date.now() + 3 * 60 * 60 * 1000; // 3 hours
  const item = { sessionId, expiry };

  try {
    localStorage.setItem(SESSION_LOCAL_STORAGE_KEY, JSON.stringify(item));
  } catch (e) {
    // localStorage failed
  }

  setToSessionStorage(SESSION_LOCAL_STORAGE_KEY, sessionId);
  setToCookie(SESSION_COOKIE_KEY, sessionId);
  memoryStorage.sessionId = sessionId;
  memoryStorage.expiry = expiry;
};

export const getOrCreateSessionId = (): string => {
  let sessionId = getSessionId();
  if (!sessionId) {
    sessionId = generateUUID();
    setSessionId(sessionId);
  }
  return sessionId;
};
